
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {closestPointOnPolyline} from "./polylineGeometry.js";

// Shared geometric core for the "Perpendicular / Offset geometry" tool
// family - Vinkelret punkt, Vinkelret linje, and Parallelforskydning (see
// tools/perpendicularCapture.js and tools/parallelOffsetTool.js for the
// capture flows that drive this every frame):
//
//   Perpendicular / Offset geometry
//           |
//           +-- computeSignedOffsetToBaseline() - shared XY projection +
//           |   signed-distance core, used by all three tools
//           |
//           +-- computePerpendicularConstraint() - Vinkelret punkt/linje
//           |
//           +-- (geometry/polylineOffset.js's offsetPolyline()) - Parallelforskydning
//
// Segment selection is deliberately based on orthogonal XY projection
// distance (via closestPointOnPolyline), never on nearest-vertex: two
// segments meeting at a bend can point in very different directions, and
// "nearest vertex" ignores that entirely. Reusing closestPointOnPolyline
// unchanged - by flattening every point's Z to 0 first - means this needs no
// separate 2D projection implementation: with every Z equal, that function's
// existing 3D distance/projection math reduces exactly to the XY-plane case
// required here. Segment choice is also deterministic: closestPointOnPolyline
// keeps the first (lowest-index) segment on an exact distance tie (strict
// "<" comparison), so a cursor position exactly at a shared vertex between
// two segments always resolves to the same one, run after run - no flicker
// from tie-breaking, and no special-casing needed for vertices.

function flattenXY(p){
	return new THREE.Vector3(p.x, p.y, 0);
}

// Given a picked 3D position and the baseline polyline's vertices (world
// space, >=2 points), finds the nearest baseline segment (by orthogonal XY
// distance) and the picked point's signed perpendicular XY distance from it.
// Shared by computePerpendicularConstraint() below (Vinkelret punkt/linje)
// and geometry/polylineOffset.js's caller (Parallelforskydning - see
// tools/parallelOffsetTool.js), so "which side, how far" is computed exactly
// once, the same way, for every tool in this family.
//
// Returns null if the baseline has fewer than 2 points, or the selected
// segment is degenerate (coincident endpoints in XY). Returns:
//   q              - orthogonal XY projection foot point, at the baseline's
//                     own interpolated Z (for drawing);
//   segmentIndex, t - which baseline segment q falls on, and where (0..1);
//   perp           - unit XY perpendicular to that segment (a fixed 90-degree
//                     rotation of its direction - see below for why the
//                     rotation sense doesn't matter);
//   signedDistance - the picked point's signed XY distance from q along
//                     `perp` (sign = side, magnitude = distance);
//   distance       - Math.abs(signedDistance).
export function computeSignedOffsetToBaseline(picked, baselinePoints){
	if(!baselinePoints || baselinePoints.length < 2) return null;

	const flatPicked = flattenXY(picked);
	const flatBaseline = baselinePoints.map(flattenXY);

	const nearest = closestPointOnPolyline(flatPicked, flatBaseline);
	if(!nearest) return null;

	const {point: qFlat, segmentIndex, t} = nearest;
	const segA = flatBaseline[segmentIndex];
	const segB = flatBaseline[segmentIndex + 1];
	const dir = segB.clone().sub(segA);
	if(dir.lengthSq() < 1e-12) return null;
	dir.normalize();

	// 90-degree rotation of the segment's XY direction - either rotation
	// sense works equally well here, since the signed offset below (the
	// picked point's own side of the baseline) resolves the actual direction
	const perp = new THREE.Vector2(-dir.y, dir.x);

	const offsetXY = new THREE.Vector2(flatPicked.x - qFlat.x, flatPicked.y - qFlat.y);
	const signedDistance = offsetXY.dot(perp);

	// q's real (non-flattened) position, for drawing: same XY as the flat
	// projection, Z interpolated along the baseline's own actual segment -
	// not the flattened Z=0 used only internally for the XY projection math
	const q = baselinePoints[segmentIndex].clone().lerp(baselinePoints[segmentIndex + 1], t);

	return {q, segmentIndex, t, perp, signedDistance, distance: Math.abs(signedDistance)};
}

// Given a picked 3D position and the baseline polyline's vertices, returns:
//   q           - see computeSignedOffsetToBaseline();
//   segmentIndex, t - ditto;
//   constrained - the picked point's XY coordinates replaced by the point on
//                 the perpendicular line through q that the picked point's
//                 own XY offset from q corresponds to, with Z kept exactly
//                 as picked (never interpolated from the baseline - see task
//                 requirement).
//
// Returns null under the same conditions as computeSignedOffsetToBaseline().
export function computePerpendicularConstraint(picked, baselinePoints){
	const offset = computeSignedOffsetToBaseline(picked, baselinePoints);
	if(!offset) return null;

	const {q, segmentIndex, t, perp, signedDistance} = offset;

	const constrained = new THREE.Vector3(
		q.x + perp.x * signedDistance,
		q.y + perp.y * signedDistance,
		picked.z,
	);

	return {q, segmentIndex, t, constrained};
}
